/**
 * Shared auth error mapping for the sign-in / sign-up routes.
 *
 * The API surfaces failures either as RFC7807 ProblemDetails (FluentValidation
 * field-error strings, in English) or as a single business `error` code. Both
 * sign-in and sign-up translate those into localized Arabic messages rather
 * than leaking raw English server text, so the message→key mapping lives here.
 */
export type AuthValidationKey =
  | 'required'
  | 'invalidEmail'
  | 'passwordTooShort'
  | 'passwordMismatch'
  | 'passwordRequiresUpper'
  | 'passwordRequiresLower'
  | 'passwordRequiresDigit'
  | 'passwordRequiresSymbol'
  | 'emailOrPasswordInvalid'
  | 'accountLocked'

/**
 * Maps a known English server validation string to a localized i18n key.
 * Returns `null` for anything unrecognized so the caller can fall back to a
 * generic message rather than rendering raw English.
 */
export function mapServerMessageToKey(message: string): AuthValidationKey | null {
  if (message === 'Passwords do not match.') return 'passwordMismatch'
  const m = message.toLowerCase()
  if (m.includes('uppercase')) return 'passwordRequiresUpper'
  if (m.includes('lowercase')) return 'passwordRequiresLower'
  if (m.includes('digit')) return 'passwordRequiresDigit'
  if (m.includes('non-alphanumeric') || m.includes('symbol')) return 'passwordRequiresSymbol'
  if (m.includes('at least 8') || m.includes('minimumlength') || m.includes('must be at least'))
    return 'passwordTooShort'
  if (m.includes('valid email') || m.includes('not a valid email address')) return 'invalidEmail'
  if (m.includes('must not be empty') || m.includes('is required')) return 'required'
  return null
}
